import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ModalService } from '../../shared/services/modal.service';

@Component({
  selector: 'app-coupon',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './coupon.component.html',
  styleUrl: './coupon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponComponent implements OnInit, OnChanges, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  @Input() availableCoupons: any;
  @Input() currentSelectedCoupon: any = null; // Currently applied coupon from parent
  showCouponSection = true;
  isValid = false;
  checked = false;
  selectedCoupon: any = null;
  manualCouponCode: string = ''; // For manual coupon code entry

  constructor(
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.modalService.openModal('coupon-popup', () => this.onCancel());
    this.initializeSelectedCoupon();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only process if the relevant inputs actually changed
    if (
      changes['currentSelectedCoupon']?.currentValue !==
        changes['currentSelectedCoupon']?.previousValue ||
      changes['availableCoupons']?.currentValue !==
        changes['availableCoupons']?.previousValue
    ) {
      this.initializeSelectedCoupon();
      this.cdr.markForCheck();
    }
  }

  initializeSelectedCoupon(): void {
    // If there's a currently selected coupon, find it in the available coupons list
    if (this.currentSelectedCoupon && this.availableCoupons?.length > 0) {
      const matchedCoupon = this.availableCoupons.find(
        (coupon: any) =>
          coupon.coupon_code === this.currentSelectedCoupon.coupon_code ||
          coupon.row_id === this.currentSelectedCoupon.row_id,
      );
      if (matchedCoupon) {
        this.selectedCoupon = matchedCoupon;
      }
    }
  }

  selectCoupon(coupon: any) {
    if (coupon.isActive) {
      // Toggle: if clicking the same coupon, unselect it
      if (this.selectedCoupon === coupon) {
        this.selectedCoupon = null;
      } else {
        this.selectedCoupon = coupon;
        // Clear manual input when selecting from list
        this.manualCouponCode = '';
      }
      this.cdr.markForCheck();
    }
  }

  /**
   * Check if Apply button should be enabled
   */
  canApplyCoupon(): boolean {
    return !!(this.selectedCoupon || this.manualCouponCode.trim());
  }

  /**
   * Handle manual coupon input changes
   */
  onManualCouponChange(): void {
    // Clear selected coupon when typing manually
    if (this.manualCouponCode.trim()) {
      this.selectedCoupon = null;
    }
    this.cdr.markForCheck();
  }

  applyCoupon() {
    if (this.selectedCoupon) {
      this.save.emit(this.selectedCoupon);
      this.close.emit();
    } else if (this.manualCouponCode.trim()) {
      // For manual entry, try to find matching coupon in available list
      const matchedCoupon = this.availableCoupons?.find(
        (coupon: any) =>
          coupon.coupon_code?.toLowerCase() ===
          this.manualCouponCode.trim().toLowerCase(),
      );

      if (matchedCoupon) {
        // If found in list, emit the full coupon object
        this.save.emit(matchedCoupon);
      } else {
        // If not in list, emit the code for backend validation
        this.save.emit({ coupon_code: this.manualCouponCode.trim() });
      }
      this.close.emit();
    }
  }

  removeCoupon() {
    // Emit null to indicate coupon removal
    this.save.emit(null);
    this.close.emit();
  }

  onCancel(): void {
    this.modalService.closeModal('coupon-popup');
    this.close.emit();
  }

  onClose(): void {
    this.modalService.closeModal('coupon-popup');
    this.close.emit();
  }

  /**
   * TrackBy function for ngFor to improve performance
   * Tracks coupons by their unique identifier
   */
  trackByCoupon(index: number, coupon: any): any {
    return coupon.row_id || coupon.coupon_code || index;
  }

  ngOnDestroy(): void {
    this.modalService.closeModal('coupon-popup');
  }
}
